import React, { useContext, useState, useEffect,useRef } from "react";

import HotelDetailsBox from "../../../components/HotelDetailsBox/HotelDetailsBox";
import {
  Row,
  Card,
  Col,
  Form,
  Input,
  DatePicker,
  Select,
  Skeleton,
  message,
  Radio,
  Checkbox,
  Result,
  Button,
  Space,
} from "antd";

import HotelFairBox from "./HotelFairBox";
import { GlobalStatesContext } from "../../../common/providers";

import { useAuthContext } from "../../../common/providers/AuthProvider";
import { useHotelContext } from "../../../common/providers/Hotels/HotelProvider";
import { getPassengerData } from "../../../helpers/PassegerData";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import HotelBookPayCard from "./HotelBookPayCard";
import CountryList from "../../../common/CountryList";
import { useNavigate } from "react-router-dom";
import queryString from "query-string";
import {
  applyCheckoutDisplayTotalsToHotels,
  resolveCheckoutHotelsWithDisplayPricing,
} from "../hotelDisplayPrice";
import { isMasterMemberAgent } from "../hotelMasterMember";
import {
  hotelGuestNameRules,
  readHotelCheckoutGuestDraft,
  writeHotelCheckoutGuestDraft,
} from "./hotelCheckoutDraft";
import {
  isInternationalHotelCheckoutReview,
  isPassportRequiredByNationality,
  collectPassportCachePayloads,
} from "../../../common/AllTickets/Hotel/hotelhelper";
import {
  isRatePlanPanRequired,
  applyMealCategoryFromRoomPlan,
  applyRoomPlanSelectionFlagsToHotels,
  getRoomMealCategoryLabel,
} from "../hotelMealTypes";
import { applySelectedRoomIdentityToHotels } from "../hotelRoomDisplay";
import ApiClient from "../../../helpers/ApiClient";
import HotelSearchLoadingOverlay from "../HotelResults/HotelSearchLoadingOverlay";
import "./HotelCheckout.scss";
import PanVerification from "./PanVerification";
import RoomDetailsCard from "../HotelDet/RoomDetailsCard";
import HotelSpecialRequest from "../HotelSpecialRequest/HotelSpecialRequest";
import {
  isSyntheticHotelCancellationPolicy,
  isHotelRatePlanNonRefundableByApi,
  getHotelNonRefundableCheckoutPolicies,
  getHotelNonRefundableCheckoutBulletLines,
  resolveHotelApiCancellationDeadline,
  finalizeCheckoutHotelCancellationDisplay,
} from "../hotelCancellation";
import { parseRoomGuestsParam } from "../HotelResults/hotelSearchParams";

const formatCountryDialLabel = (item) => `${item.dial_code} ${item.name}`;

/** Prefer YYYY-MM-DD prefix so ISO timestamps do not shift +1 day in IST. */
const stayDateOnly = (dateInput) => {
  if (dateInput == null || dateInput === "") return "";
  const raw = Array.isArray(dateInput) ? dateInput[0] : dateInput;
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const ymd = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return ymd ? ymd[1] : s.slice(0, 10);
};

const normalizeDialCode = (code) => {
  const raw = String(code ?? "").trim();
  if (!raw) return "";
  return raw.startsWith("+") ? raw : `+${raw}`;
};

const { Option } = Select;

const HOTEL_CHECKOUT_LOADING_MESSAGES = [
  "Confirming your room details…",
  "Checking availability and latest price…",
  "Reviewing cancellation policy…",
  "Almost there — preparing your checkout…",
];

const requiresPanFromGuestFields = (guestRequiredFields = [], fallbackNationality = "IN") => {
  const rows = Array.isArray(guestRequiredFields) ? guestRequiredFields : [];
  if (rows.some((g) => g?.pan === true || g?.PAN === true || g?.Pan === true)) {
    return true;
  }
  if (rows.some((g) => g?.pan === false || g?.PAN === false || g?.Pan === false)) {
    return false;
  }
  return String(fallbackNationality ?? "IN").trim().toUpperCase() === "IN";
};

/** Selected checkout rate(s): false = all PAN optional; true = any required; null = unknown. */
const resolvePanRequiredFromCheckoutRooms = (rooms = []) => {
  if (!Array.isArray(rooms) || rooms.length === 0) return null;
  let sawExplicit = false;
  let anyRequired = false;
  rooms.forEach((room) => {
    const plans = Array.isArray(room?.ratePlans) ? room.ratePlans : [];
    const sources = plans.length > 0 ? plans : [room];
    sources.forEach((src) => {
      if (!src || typeof src !== "object") return;
      const hasFalse =
        src.panRequired === false ||
        src.PanRequired === false ||
        src.pan === false ||
        src.PAN === false ||
        src.ipr === false ||
        src.IPR === false;
      const hasTrue = isRatePlanPanRequired(src, room);
      if (hasFalse || hasTrue || src.panRequired === true || src.PanRequired === true) {
        sawExplicit = true;
      }
      if (hasTrue) anyRequired = true;
    });
  });
  if (!sawExplicit) return null;
  return anyRequired;
};

const applyPanRequiredForCheckout = ({
  guestRequiredFields,
  nationality,
  setIsPanRequired,
  isMasterMember = false,
  isAgent = false,
  isGuest = false,
  isInternationalHotel = false,
  rooms = [],
}) => {
  // Agents / Master: never guest-form PAN on checkout (Hold has no PAN; Instant/Pay&Book
  // uses ticket Generate Voucher or Review PAN — not this Guest Details UI).
  if (isMasterMember || isAgent) {
    setIsPanRequired(false);
    return;
  }
  // Non-India nationality → passport only (never PAN + passport together).
  if (isPassportRequiredByNationality(nationality)) {
    setIsPanRequired(false);
    return;
  }
  // Guest (RoleType = 4 / without login) + International hotel + India nationality:
  // PAN always mandatory.
  if (isGuest && isInternationalHotel) {
    setIsPanRequired(true);
    return;
  }
  // Domestic guests: only when selected rate is PAN Required.
  if (!isInternationalHotel) {
    const roomPan = resolvePanRequiredFromCheckoutRooms(rooms);
    setIsPanRequired(roomPan === true);
    return;
  }
  const roomPan = resolvePanRequiredFromCheckoutRooms(rooms);
  if (roomPan === false) {
    setIsPanRequired(false);
    return;
  }
  const pan =
    roomPan === true
      ? true
      : requiresPanFromGuestFields(guestRequiredFields, nationality);
  setIsPanRequired(Boolean(pan));
};

const HotelCheckout = () => {
  const { setHotelCheckOutData } = useHotelContext();
  const {
    user,
    isLogin: { agent },
  } = useAuthContext();
  const isMasterMember = isMasterMemberAgent(
    user?.Role?.RoleId,
    user?.Membership,
  );
  const {
    state: {
      otherData: { insuranceData, selectedInsuranceData,PanVerificationDetails },
    },

    setSelectedInsuranceData,
    RemovePromoConvFee,
  } = useContext(GlobalStatesContext);
  let navigate = useNavigate();
  const [contact_form] = Form.useForm();
  const [guestDetailsForm] = Form.useForm();
  const [ids, setIds] = useState({
    traceId: null,
    repriceId: null,
  });
  const [loading, setLoading] = useState(true);
  const [checkoutError, setCheckoutError] = useState("");
  const [hotelPriceData, setHotelPriceData] = useState({});
  const [selectedHotelName, setSelectedHotelName] = useState("");
  const [SamePanNumber, setSamePanNumber] = useState(false);
  const [panVerificationDetail, setPanVerificationDetails] = useState([]);
  const [isPanRequired, setIsPanRequired] = useState(false);

  const [hotelSearchData, setHotelSearchData] = useState({});

  const [roomGuestInfo, setRoomGuestInfo] = useState([]);
  const [specialRequest, setSpecialRequest] = useState("");
  const scnpEnrichAttemptedRef = useRef(false);

  const ratePlanHasDisplayableCancellationData = (rp) => {
    if (isHotelRatePlanNonRefundableByApi(rp)) {
      return (
        getHotelNonRefundableCheckoutPolicies(rp).length > 0 ||
        getHotelNonRefundableCheckoutBulletLines(rp).length > 0 ||
        String(
          rp?.supplierCancellationNarrative ??
            rp?.SupplierCancellationNarrative ??
            "",
        ).trim().length > 0
      );
    }
    const text = String(
      rp?.supplierCancellationNarrative ??
        rp?.SupplierCancellationNarrative ??
        ""
    ).trim();
    // "Non-refundable" leftover from a wrong hotelprice match is not usable FC copy.
    const isNrOnlyScnp = /^non[\s-]*refundable\.?$/i.test(text);
    const hasFcNarrative =
      text.length > 0 &&
      !isNrOnlyScnp &&
      /free|0%|refund|cancel for/i.test(text) &&
      !/non[\s-]*refund/i.test(text);
    const hasRealSlabs = (rp?.cancellationPolicy ?? []).some(
      (p) =>
        p &&
        !isSyntheticHotelCancellationPolicy(p) &&
        (parseFloat(String(p?.penaltyAmount ?? "").trim()) === 0 ||
          String(p?.fromDate ?? "") !== String(p?.toDate ?? "")),
    );
    return hasFcNarrative || hasRealSlabs;
  };

  const ratePlansHaveCheckoutCancellationData = (rooms = []) =>
    (Array.isArray(rooms) ? rooms : []).some((room) =>
      (room?.ratePlans ?? []).some(ratePlanHasDisplayableCancellationData)
    );

  const resolveSourceRatePlan = (srcRoom, rp, plan = {}) => {
    const plans = srcRoom?.ratePlans ?? [];
    const exact = plans.find(
      (s) => String(s?.ratePlanId) === String(rp?.ratePlanId)
    );
    if (exact) return exact;
    const planRateId = String(plan?.rateID ?? plan?.rateId ?? "").trim();
    if (planRateId) {
      const byPlanId = plans.find((s) => String(s?.ratePlanId) === planRateId);
      if (byPlanId) return byPlanId;
    }
    // FC selection after hotelprice rate-id rotate — prefer a free-cancel source plan.
    if (plan?.refundable === true || plan?.Refundable === true) {
      const withFree = plans.find((s) =>
        (s?.cancellationPolicy ?? []).some(
          (p) =>
            p &&
            !isSyntheticHotelCancellationPolicy(p) &&
            parseFloat(String(p?.penaltyAmount ?? "").trim()) === 0,
        ),
      );
      if (withFree) return withFree;
      const refundable = plans.find(
        (s) => s?.refundable === true || s?.Refundable === true,
      );
      if (refundable) return refundable;
    }
    return null;
  };

  const findSourceRoomForCancellationMerge = (
    sourceRooms = [],
    room = {},
    plan = {},
  ) => {
    const planRoomId = String(plan?.roomID ?? plan?.roomId ?? "").trim();
    const byId =
      sourceRooms.find((s) => String(s?.roomId) === String(room?.roomId)) ||
      sourceRooms.find(
        (s) => String(s?.roomTypeId) === String(room?.roomTypeId),
      ) ||
      (planRoomId
        ? sourceRooms.find((s) => String(s?.roomTypeId) === planRoomId)
        : null) ||
      (planRoomId
        ? sourceRooms.find((s) => String(s?.roomId) === planRoomId)
        : null);
    if (byId) return byId;

    // Only for explicit FC selection — never invent FC onto NR picks.
    if (!(plan?.refundable === true || plan?.Refundable === true)) {
      return null;
    }
    const wantName = String(
      plan?.displayTitle ?? plan?.roomName ?? room?.roomName ?? "",
    )
      .trim()
      .toLowerCase();
    const target = Math.ceil(Number(plan?.displayTotal ?? 0));
    let best = null;
    let bestScore = -Infinity;
    sourceRooms.forEach((s) => {
      (s?.ratePlans ?? []).forEach((srp) => {
        if (srp?.refundable === false || srp?.Refundable === false) return;
        const policies = (srp?.cancellationPolicy ?? []).filter(
          (p) => p && !isSyntheticHotelCancellationPolicy(p),
        );
        const hasFree = policies.some(
          (p) => parseFloat(String(p?.penaltyAmount ?? "").trim()) === 0,
        );
        if (!hasFree && srp?.refundable !== true && srp?.Refundable !== true) {
          return;
        }
        const label = String(s?.roomName ?? s?.rc ?? "")
          .trim()
          .toLowerCase();
        let score = hasFree ? 50 : 10;
        if (wantName && label) {
          if (label === wantName) score += 30;
          else if (label.includes(wantName) || wantName.includes(label)) {
            score += 15;
          }
        }
        if (target > 0) {
          const totals = [
            srp?.price?.customerDisplayTotal,
            srp?.price?.total,
            srp?.price?.tripjackSupplierGross,
            srp?.price?.supplierGross,
          ]
            .map((v) => Math.ceil(Number(v) || 0))
            .filter((n) => n > 0);
          if (totals.length > 0) {
            const diff = Math.min(...totals.map((t) => Math.abs(t - target)));
            if (diff <= Math.max(200, Math.ceil(target * 0.05))) {
              score += 40 - Math.min(diff, 40);
            }
          }
        }
        if (score > bestScore) {
          bestScore = score;
          best = s;
        }
      });
    });
    return best;
  };

  const mergeCancellationNarrativeFromSource = (
    targetRooms = [],
    sourceRooms = [],
    roomPlan = []
  ) => {
    if (!Array.isArray(targetRooms) || !Array.isArray(sourceRooms)) {
      return targetRooms;
    }
    const plans = Array.isArray(roomPlan) ? roomPlan : [];
    const isNrOnlyScnp = (text) =>
      /^non[\s-]*refundable\.?$/i.test(String(text ?? "").trim());
    return targetRooms.map((room, roomIndex) => {
      const plan = plans[roomIndex] ?? plans[0] ?? {};
      // Never fall back to sourceRooms[0] — that copies Free Cancel text onto NR rates.
      const srcRoom = findSourceRoomForCancellationMerge(
        sourceRooms,
        room,
        plan,
      );
      if (!srcRoom) {
        return room;
      }
      const mergedPlans = (room?.ratePlans ?? []).map((rp) => {
        const srcRp = resolveSourceRatePlan(srcRoom, rp, plan);
        if (!srcRp) {
          return rp;
        }
        // Selected Non-Refundable rate must stay NR on checkout (do not enrich to FC).
        if (rp?.refundable === false || rp?.Refundable === false) {
          const mealPlanNr =
            rp?.mealPlan ||
            rp?.MealPlan ||
            srcRp?.mealPlan ||
            srcRp?.MealPlan ||
            srcRp?.ratePlanName ||
            srcRp?.RatePlanName ||
            "";
          const ratePlanNameNr =
            rp?.ratePlanName ||
            rp?.RatePlanName ||
            srcRp?.ratePlanName ||
            srcRp?.RatePlanName ||
            mealPlanNr ||
            "";
          const targetPolicies = (rp?.cancellationPolicy ?? []).filter(Boolean);
          const srcPolicies = (srcRp?.cancellationPolicy ?? []).filter(Boolean);
          const policies =
            targetPolicies.length > 0 ? targetPolicies : srcPolicies;
          const narrative = String(
            rp?.supplierCancellationNarrative ??
              rp?.SupplierCancellationNarrative ??
              srcRp?.supplierCancellationNarrative ??
              srcRp?.SupplierCancellationNarrative ??
              ""
          ).trim();
          return {
            ...rp,
            refundable: false,
            Refundable: false,
            tripjackIfra: false,
            ...(mealPlanNr ? { mealPlan: mealPlanNr, MealPlan: mealPlanNr } : {}),
            ...(ratePlanNameNr ? { ratePlanName: ratePlanNameNr } : {}),
            ...(policies.length > 0 ? { cancellationPolicy: policies } : {}),
            ...(narrative ? { supplierCancellationNarrative: narrative } : {}),
          };
        }
        let narrative = String(
          rp?.supplierCancellationNarrative ??
            rp?.SupplierCancellationNarrative ??
            srcRp?.supplierCancellationNarrative ??
            srcRp?.SupplierCancellationNarrative ??
            ""
        ).trim();
        if (isNrOnlyScnp(narrative)) {
          const srcNar = String(
            srcRp?.supplierCancellationNarrative ??
              srcRp?.SupplierCancellationNarrative ??
              "",
          ).trim();
          narrative = isNrOnlyScnp(srcNar) ? "" : srcNar;
        }
        const srcPolicies = (srcRp?.cancellationPolicy ?? []).filter(
          (p) => p && !isSyntheticHotelCancellationPolicy(p)
        );
        const targetPolicies = (rp?.cancellationPolicy ?? []).filter(
          (p) => p && !isSyntheticHotelCancellationPolicy(p)
        );
        const targetHasFree = targetPolicies.some(
          (p) => parseFloat(String(p?.penaltyAmount ?? "").trim()) === 0,
        );
        // Prefer source FC slabs when hotelprice room lost them.
        const policies =
          targetHasFree && targetPolicies.length > 0
            ? targetPolicies
            : srcPolicies.length > 0
              ? srcPolicies
              : targetPolicies;
        const hasFreeSlab = policies.some(
          (p) => parseFloat(String(p?.penaltyAmount ?? "").trim()) === 0
        );
        const refundable =
          rp?.refundable === true ||
          plan?.refundable === true ||
          plan?.Refundable === true ||
          (srcRp?.refundable === true && rp?.refundable !== false) ||
          hasFreeSlab ||
          (narrative.length > 0 &&
            /free|refund|flexible|cancel for/i.test(narrative) &&
            !/non[\s-]*refund/i.test(narrative));
        const mealPlan =
          rp?.mealPlan ||
          rp?.MealPlan ||
          srcRp?.mealPlan ||
          srcRp?.MealPlan ||
          srcRp?.ratePlanName ||
          srcRp?.RatePlanName ||
          "";
        const ratePlanName =
          rp?.ratePlanName ||
          rp?.RatePlanName ||
          srcRp?.ratePlanName ||
          srcRp?.RatePlanName ||
          mealPlan ||
          "";
        const mergedPlan = {
          ...rp,
          ...(mealPlan ? { mealPlan, MealPlan: mealPlan } : {}),
          ...(ratePlanName ? { ratePlanName } : {}),
          ...(narrative ? { supplierCancellationNarrative: narrative } : {}),
          ...(policies.length > 0 ? { cancellationPolicy: policies } : {}),
          ...(refundable
            ? { refundable: true, Refundable: true, tripjackIfra: true }
            : {}),
        };
        if (refundable) {
          const apiCutoff = resolveHotelApiCancellationDeadline(mergedPlan);
          if (apiCutoff) {
            const iso = `${apiCutoff}T12:00:00.000Z`;
            mergedPlan.tripjackCancellationCutoff = apiCutoff;
            mergedPlan.lastCancellationDate = iso;
            mergedPlan.lastVoucherDate = iso;
          }
        }
        return mergedPlan;
      });
      return { ...room, ratePlans: mergedPlans };
    });
  };

  const handlePaxField = (val, roomIndex, paxIndex, key) => {
    let temp = [...roomGuestInfo];

    if (paxIndex === 0) {
      temp[roomIndex].paxInfoList[paxIndex]["leadGuest"] = true;
    } else {
      temp[roomIndex].paxInfoList[paxIndex]["leadGuest"] = false;
    }

    temp[roomIndex].paxInfoList[paxIndex][key] = val;

    setRoomGuestInfo(temp);
  };

  const [insuranceRequired, setInsuranceRequired] = useState(-1);

  const loadpassengerData = () => {
    const hotelParams = queryString.parse(window.location.search);
    const draft = readHotelCheckoutGuestDraft(
      hotelParams?.traceId,
      hotelParams?.hotelId,
    );
    if (draft?.addressInfo) {
      contact_form.setFieldsValue({
        phoneNo: draft.addressInfo.phoneNo,
        email: draft.addressInfo.email,
        areaCode: draft.addressInfo.areaCode || "+91",
        addressLine1: draft.addressInfo.addressLine1 || "",
        city: draft.addressInfo.city || "",
        state: draft.addressInfo.state || "",
        countryCode: draft.addressInfo.countryCode,
        zipCode: draft.addressInfo.zipCode,
      });
      return;
    }
    if (user && user?.UserID) {
      getPassengerData(user.UserID).then((data) => {
        if (data.status) {
          contact_form.setFieldsValue({
            phoneNo: data.Mobile,
            email: data.Email,
            areaCode: normalizeDialCode(data?.DailingCode) || "+91",
            addressLine1: data?.Address1 ? data?.Address1 : "",
            city: data?.city ? data?.city : "",
            state: data?.state ? data?.state : "",
          });
        }
      });
    }
  };


  // Function to set initial values based on panVerificationDetail
  const setInitialFormValues = () => {
    const initialValues = {};

    // Build your initial values object here, for example:
    panVerificationDetail.forEach(detail => {
      const firstNameField = `firstname_${detail.roomIndex}_${detail.paxIndex}`;
      const lastNameField = `lastname_${detail.roomIndex}_${detail.paxIndex}`;
      const prefixNameField = `Title_${detail.roomIndex}_${detail.prefix}`;

      const firstNameValue = detail?.first_name.trim();
      const lastNameValue = detail?.last_name.trim();
      const prefixValue = detail?.prefix;

      // if(prefixValue == 'Mr' || prefixValue == 'Mrs'){
      //   initialValues[prefixNameField] = prefixValue;
      // }
      let temp = [...roomGuestInfo];
      if(Array.isArray(temp) && temp.length > 0){
      if (detail.paxIndex === 0) {
        temp[detail.roomIndex].paxInfoList[detail.paxIndex]["leadGuest"] = true;
      } else {
        temp[detail.roomIndex].paxInfoList[detail.paxIndex]["leadGuest"] = false;
      }
    }
  
     
  
   
    
      if (!firstNameValue && lastNameValue) {
        // First name is blank, but last name has a value
        initialValues[firstNameField] = lastNameValue;
        initialValues[lastNameField] = lastNameValue;
      } else if (firstNameValue && !lastNameValue) {
        // Last name is blank, but first name has a value
        initialValues[firstNameField] = firstNameValue;
        initialValues[lastNameField] = firstNameValue;
      } else {
        // Both first name and last name have values
        initialValues[firstNameField] = firstNameValue;
        initialValues[lastNameField] = lastNameValue;
      }
      if(Array.isArray(temp) && temp.length > 0){
      temp[detail.roomIndex].paxInfoList[detail.paxIndex]["firstName"] = initialValues[firstNameField];
      temp[detail.roomIndex].paxInfoList[detail.paxIndex]["lastName"] = initialValues[lastNameField];
      setRoomGuestInfo(temp);
    }
    });

    // Set the initial values on the form
    guestDetailsForm.setFieldsValue(initialValues);
  };

  const prevPanVerificationDetails = useRef();
  useEffect(() => {

    if (PanVerificationDetails &&
      typeof PanVerificationDetails === 'object' &&
      (!('isloading' in PanVerificationDetails) || PanVerificationDetails?.current?.isLoading === false)) {

      if (prevPanVerificationDetails.current !== PanVerificationDetails) {
        prevPanVerificationDetails.current = PanVerificationDetails;
        const { Id, first_name, last_name, pan_number, prefix
        } = PanVerificationDetails[0];
        setPanVerificationDetails(prevDetails => [...prevDetails, {
          Id, first_name, last_name, pan_number, prefix, paxIndex: PanVerificationDetails.paxIndex,
          roomIndex: PanVerificationDetails.roomIndex
        }]);
      }
    }

  }, [PanVerificationDetails]);
  useEffect(() => {
    if (panVerificationDetail.length > 0) {
      setInitialFormValues();
    }
  }, [panVerificationDetail]);
  useEffect(() => {
    RemovePromoConvFee();
    fetchHotelPrice();
    loadpassengerData();
    setPanVerificationDetails([]);
  }, []);

  // Non-refundable rooms still need Tripjack scnp on checkout — fetch from rooms log if price omitted it.
  // Also backfill meal/board when hotelprice rooms lost mealPlan.
  useEffect(() => {
    const hotelParams = queryString.parse(window.location.search);
    const rooms = hotelPriceData?.rooms;
    if (!hotelParams?.traceId || !hotelParams?.hotelId) return;
    if (!Array.isArray(rooms) || rooms.length === 0) return;
    const needsCancellation = !ratePlansHaveCheckoutCancellationData(rooms);
    const needsMeal = rooms.some((room) => !getRoomMealCategoryLabel(room));
    if (!needsCancellation && !needsMeal) return;
    if (scnpEnrichAttemptedRef.current) return;
    scnpEnrichAttemptedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const res = await ApiClient.post("hotels-v2/hotelrooms", {
          traceId: hotelParams.traceId,
          hotelCode: hotelParams.hotelId,
          consolidationWaitTime: 20000,
          userId: user?.UserID ?? 1,
          roleType: user?.Role?.RoleId ?? 4,
          membership: user?.Membership ?? 1,
        });
        if (cancelled || res?.status !== 200) return;
        const sourceRooms = res?.data?.rooms;
        if (!Array.isArray(sourceRooms) || sourceRooms.length === 0) return;
        let parsedRoomPlan = [];
        try {
          parsedRoomPlan = hotelParams?.roomPlan
            ? JSON.parse(hotelParams.roomPlan)
            : [];
        } catch {
          parsedRoomPlan = [];
        }
        setHotelPriceData((prev) => {
          const mergedRooms = mergeCancellationNarrativeFromSource(
            prev?.rooms ?? [],
            sourceRooms,
            parsedRoomPlan
          );
          let next = { ...prev, rooms: mergedRooms };
          if (Array.isArray(parsedRoomPlan) && parsedRoomPlan.length > 0) {
            next = finalizeCheckoutHotelCancellationDisplay(
              applySelectedRoomIdentityToHotels(
                applyRoomPlanSelectionFlagsToHotels(next, parsedRoomPlan),
                parsedRoomPlan,
              ),
            );
          }
          return next;
        });
      } catch {
        // optional enrichment
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hotelPriceData?.rooms, hotelPriceData?.traceId, user?.UserID, user?.Role?.RoleId, user?.Membership]);
  const HotelGuestReqFields = (paxObj, guestRequiredFields) => {
    guestRequiredFields.map((paxReqFieldsObj) => {
      Object.keys(paxReqFieldsObj).map((paxReqKeys) => {
        if (paxReqFieldsObj[paxReqKeys] === true) {
          const normalizedKey =
            paxReqKeys === "PAN"
              ? "pan"
              : paxReqKeys === "PassportNo"
                ? "passportNo"
                : paxReqKeys;
          paxObj[normalizedKey] = "";
        }
      });
    });
    return paxObj;
  };

  const buildCheckoutCacheKey = (hotelParams) => {
    const trace = hotelParams?.traceId || "notrace";
    const hotel = hotelParams?.hotelId || hotelParams?.hotelCode || "nohotel";
    const rooms = hotelParams?.roomsId || "norooms";
    // Include selected rate identity so a prior Twin Beds NR cache never
    // hydrates after selecting Free Cancellation Standard at a different fare.
    let rateKey = "noroomplan";
    try {
      const plans = hotelParams?.roomPlan
        ? JSON.parse(hotelParams.roomPlan)
        : [];
      if (Array.isArray(plans) && plans.length > 0) {
        rateKey = plans
          .map(
            (p) =>
              `${p?.rateID || p?.rateId || ""}|${p?.roomID || p?.roomId || ""}|${p?.displayTotal || ""}`,
          )
          .join("::");
      }
    } catch {
      rateKey = "noroomplan";
    }
    return `hotel_checkout_cache_${trace}_${hotel}_${rooms}_${rateKey}`;
  };

  const checkoutHotelIdsMatch = (requestedId, responseHotel = {}) => {
    const req = String(requestedId ?? "").trim().toLowerCase();
    if (!req) return true;
    const candidates = [
      responseHotel?.hotelCode,
      responseHotel?.HotelCode,
      responseHotel?.supplierHotelCode,
      responseHotel?.SupplierHotelCode,
    ]
      .map((v) => String(v ?? "").trim().toLowerCase())
      .filter(Boolean);
    if (candidates.length === 0) return true;
    return candidates.some(
      (c) => c === req || c.includes(req) || req.includes(c),
    );
  };

  const hydrateFromCheckoutCache = (cacheKey, hotelParams = {}) => {
    try {
      // OLD (removed):
      // const raw = sessionStorage.getItem(cacheKey);
      // NEW:
      // try stable key first, then legacy roomPlan-based key for backward compatibility.
      let raw = sessionStorage.getItem(cacheKey);
      if (!raw) {
        const trace = hotelParams?.traceId || "notrace";
        const rooms = hotelParams?.roomsId || "norooms";
        const roomPlan = hotelParams?.roomPlan || "noroomplan";
        const legacyKey = `hotel_checkout_cache_${trace}_${rooms}_${roomPlan}`;
        raw = sessionStorage.getItem(legacyKey);
      }
      if (!raw) return false;
      const snapshot = JSON.parse(raw);
      if (!snapshot || typeof snapshot !== "object") return false;
      if (!snapshot.hotelPriceData || Object.keys(snapshot.hotelPriceData).length === 0) return false;
      const cachedHotelId =
        snapshot.hotelPriceData?.hotelCode ??
        snapshot.hotelPriceData?.HotelCode ??
        "";
      const requestedHotelId = hotelParams?.hotelId ?? hotelParams?.hotelCode ?? "";
      if (
        requestedHotelId &&
        cachedHotelId &&
        !checkoutHotelIdsMatch(requestedHotelId, snapshot.hotelPriceData)
      ) {
        return false;
      }

      setHotelPriceData(snapshot.hotelPriceData || {});
      setHotelSearchData(snapshot.hotelSearchData || {});
      setRoomGuestInfo(Array.isArray(snapshot.roomGuestInfo) ? snapshot.roomGuestInfo : []);
      if (snapshot.selectedHotelName) {
        setSelectedHotelName(snapshot.selectedHotelName);
      }
      const cachedGuestFields = Array.isArray(snapshot.hotelPriceData?.guestRequiredFields)
        ? snapshot.hotelPriceData.guestRequiredFields
        : [];
      applyPanRequiredForCheckout({
        guestRequiredFields: cachedGuestFields,
        nationality: hotelParams?.nationality,
        setIsPanRequired,
        isMasterMember,
        isAgent: user?.Role?.RoleId === 5 || agent,
        // Guest (RoleType = 4 / without login): PAN mandatory for International only.
        isGuest: Number(user?.Role?.RoleId ?? (agent ? 5 : 4)) === 4,
        isInternationalHotel: isInternationalHotelCheckoutReview({
          hotelPriceData: snapshot.hotelPriceData || {},
        }),
        rooms: snapshot.hotelPriceData?.rooms ?? [],
      });
      setLoading(false);
      message.warning("Live room price refresh failed. Showing last loaded checkout data.", 4);
      return true;
    } catch {
      return false;
    }
  };

  const redirectToHotelDetailsForReselect = (hotelParams = {}) => {
    // OLD (removed): roomsId first — HCI roomsId is not the search hotelCode.
    const hotelId = hotelParams?.hotelId || hotelPriceData?.hotelCode;
    const traceId = hotelParams?.traceId;
    const hotelName = hotelParams?.hotelName || selectedHotelName || "";
    if (!hotelId || !traceId) return false;

    const query = queryString.stringify({
      hotelId,
      traceId,
      hotelName,
    });
    message.warning("Selected room is no longer available. Please reselect room.", 5);
    navigate(`/hotels/hotel-details?${query}`);
    return true;
  };

  const fetchHotelPrice = () => {
    const hotelParams = queryString.parse(window.location.search);
    scnpEnrichAttemptedRef.current = false;
    const cacheKey = buildCheckoutCacheKey(hotelParams);
    setIds({
      traceId: hotelParams.traceId,
      repriceId: null,
    });
    
    if (hotelParams.hotelName) {
      setSelectedHotelName(hotelParams.hotelName);
    }

    setLoading(true);
    setCheckoutError("");
    // OLD (removed):
    // let Req = {
    //   ...hotelParams,
    //   roomPlan: JSON.parse(hotelParams.roomPlan),
    //   userId: user?.UserID ?? 1,
    //   roleType: user?.Role?.RoleId ?? 4,
    //   membership: user?.Membership ?? 1,
    // };
    // NEW:
    // URL params come in as strings; backend expects `roomGuests` array and `roomPlan` array.
    // When roomGuests is a string, backend can throw "Cannot read property 'forEach' of undefined".
    let parsedRoomPlan = [];
    try {
      parsedRoomPlan = hotelParams?.roomPlan ? JSON.parse(hotelParams.roomPlan) : [];
    } catch {
      parsedRoomPlan = [];
    }
    const checkoutDisplayTotal = Math.ceil(
      Number(hotelParams?.checkoutDisplayTotal ?? 0),
    );
    if (
      checkoutDisplayTotal > 0 &&
      Array.isArray(parsedRoomPlan) &&
      parsedRoomPlan.length > 0
    ) {
      // Details-page total is authoritative — prevents cross-tab traceId price bleed.
      parsedRoomPlan = parsedRoomPlan.map((plan, index) =>
        index === 0 ? { ...plan, displayTotal: checkoutDisplayTotal } : plan,
      );
    }
    // Keep displayTotal for Master too — stripping it let backend rematch a
    // cheaper Non-refundable rate and overwrite Free Cancellation selection.
    const apiRoomPlan = Array.isArray(parsedRoomPlan) ? parsedRoomPlan : [];
    let parsedRoomGuests = parseRoomGuestsParam(hotelParams?.roomGuests, []);

    let Req = {
      ...hotelParams,
      hotelId: hotelParams?.hotelId ?? hotelParams?.hotelCode,
      hotelCode: hotelParams?.hotelId ?? hotelParams?.hotelCode,
      ...(checkoutDisplayTotal > 0 ? { checkoutDisplayTotal } : {}),
      roomPlan: Array.isArray(apiRoomPlan) ? apiRoomPlan : [],
      roomGuests: Array.isArray(parsedRoomGuests) ? parsedRoomGuests : [],
      userId: user?.UserID ?? 1,
      roleType: user?.Role?.RoleId ?? 4,
      membership: user?.Membership ?? 1,
    };
    setHotelPriceData({});
    setHotelSearchData({});
    setRoomGuestInfo([]);
    ApiClient.post("hotels-v2/hotelprice", Req)
      .then((res) => res)
      .then((res) => {
        // Nest returns { status, data: { hotels, traceId, repriceId } }; ApiClient may flatten or nest.
        const inner =
          res?.data?.hotels || res?.data?.Hotels
            ? res.data
            : res?.hotels || res?.Hotels
              ? res
              : res?.data ?? res;
        const payload = inner;
        const hotels = payload?.hotels ?? payload?.Hotels;
        const hasUsableHotelsData =
          hotels &&
          Object.keys(hotels).length > 0 &&
          Array.isArray(hotels?.rooms) &&
          hotels.rooms.length > 0;

        // OLD (removed): any HTTP 400 → fail — blocked checkout when backend returned 400 + message but rooms existed in data.
        const roomMatchFailed =
          !hasUsableHotelsData &&
          (res?.status === 404 ||
            /selected room could not be matched|selected rooms not found|room session not found/i.test(
              String(res?.message ?? payload?.message ?? "")
            ));

        if (roomMatchFailed) {
          if (hydrateFromCheckoutCache(cacheKey, hotelParams)) {
            return;
          }
          const errMsg =
            res?.message ||
            payload?.message ||
            "Selected room could not be loaded. Please go back and select the room again.";
          setCheckoutError(errMsg);
          message.error(errMsg, 6);
          setLoading(false);
          return;
        }

        if (payload && (res.status === 200 || res.status === 201 || hasUsableHotelsData)) {
          const { purchaseType, repriceId, traceId, tripjackIsBA } = payload;
          
          // OLD (removed):
          // if (res.data.errors && res.data.errors.length > 0) {
          //   ...
          //   setLoading(false);
          //   return;
          // }
          // NEW:
          // if API has partial errors but still returns usable hotels/rooms,
          // continue flow instead of hard failing with "Room Not Available".
          if (payload.errors && payload.errors.length > 0) {
            payload.errors.forEach((err) => {
              if (err.errorCode === "SOLDOUT" || err.errorDetail) {
                message.error(err.errorDetail || err.message || 'Room not available', 5);
              }
            });
            if (!hasUsableHotelsData) {
              // OLD (removed): redirectToHotelDetailsForReselect — bounced user back to hotel-details
              // immediately so checkout never appeared to open.
              if (hydrateFromCheckoutCache(cacheKey, hotelParams)) {
                return;
              }
              setCheckoutError(
                payload?.message ||
                  res?.message ||
                  "This room is sold out or no longer available from the supplier."
              );
              setLoading(false);
              return;
            }
          }
          
          if (hotels && Object.keys(hotels).length > 0) {
            const requestedHotelId =
              hotelParams?.hotelId ?? hotelParams?.hotelCode ?? "";
            if (
              requestedHotelId &&
              !checkoutHotelIdsMatch(requestedHotelId, hotels)
            ) {
              message.warning(
                "Price session was for a different hotel. Using your selected room fare.",
                5,
              );
            }
            // OLD (removed):
            // if (!res.data.errors || res.data.errors?.length === 0) {
            // NEW:
            // proceed when hotels data is usable even if errors[] exists.
            if ((!payload.errors || payload.errors?.length === 0) || hasUsableHotelsData) {
              const { hotels: pricedHotels, roomPlan: effectiveRoomPlan } =
                resolveCheckoutHotelsWithDisplayPricing(hotels, parsedRoomPlan, {
                  checkInDate: hotelParams?.checkInDate,
                  roleType: user?.Role?.RoleId,
                  membership: user?.Membership,
                });
              const hotelsWithPinnedFare = applyCheckoutDisplayTotalsToHotels(
                pricedHotels,
                effectiveRoomPlan,
              );
              const hotelsWithMeal = applyMealCategoryFromRoomPlan(
                hotelsWithPinnedFare,
                effectiveRoomPlan,
              );
              const hotelsWithSelectionFlags = finalizeCheckoutHotelCancellationDisplay(
                applySelectedRoomIdentityToHotels(
                  applyRoomPlanSelectionFlagsToHotels(
                    hotelsWithMeal,
                    effectiveRoomPlan,
                  ),
                  effectiveRoomPlan,
                ),
              );
              setHotelPriceData({
                ...hotelsWithSelectionFlags,
                purchaseType,
                // Tripjack isBA: true = Hold/Block allowed; false = Instant Book only.
                tripjackIsBA:
                  tripjackIsBA === true
                    ? true
                    : tripjackIsBA === false
                      ? false
                      : payload.tripjackIsBA ?? hotelsWithSelectionFlags?.tripjackIsBA,
                traceId: traceId ?? payload.traceId,
                repriceId,
                tripjackBookingId: payload.tripjackBookingId ?? null,
                reviewReady: payload.reviewReady ?? false,
              });
              
              const roomGuestsData = parseRoomGuestsParam(
                hotelParams?.roomGuests,
                [{ noOfAdults: 1, noOfChilds: 0, childAge: [] }],
              );
              
              const stayCheckIn = stayDateOnly(
                hotelParams?.checkInDate ??
                  hotelsWithSelectionFlags?.checkInDate ??
                  hotelsWithSelectionFlags?.checkIn,
              );
              const stayCheckOut = stayDateOnly(
                hotelParams?.checkOutDate ??
                  hotelsWithSelectionFlags?.checkOutDate ??
                  hotelsWithSelectionFlags?.checkOut,
              );
              const requestData = {
                checkInDate: stayCheckIn,
                checkOutDate: stayCheckOut,
                checkIn: stayCheckIn,
                checkOut: stayCheckOut,
                roomGuests: roomGuestsData,
                roomPlan: Array.isArray(parsedRoomPlan) ? parsedRoomPlan : [],
                nationality: hotelParams?.nationality ?? "IN",
              };
              
              setHotelSearchData(requestData);
              const guestDraft = readHotelCheckoutGuestDraft(
                hotelParams?.traceId,
                hotelParams?.hotelId,
              );
              if (!guestDraft) {
                setHotelCheckOutData({});
              }
              const roomInfoArr = [];

              let roomsInfo = requestData.roomGuests;

              const guestRequiredFieldsForPax = Array.isArray(
                hotelsWithSelectionFlags?.guestRequiredFields,
              )
                ? hotelsWithSelectionFlags.guestRequiredFields
                : Array.isArray(hotelsWithSelectionFlags?.GuestRequiredFields)
                  ? hotelsWithSelectionFlags.GuestRequiredFields
                  : [];

              roomsInfo.map((room, roomIndex) => {
                const paxListArr = [];
                const savedPax = guestDraft?.guests?.[roomIndex]?.paxInfoList ?? [];

                [...Array(room.noOfAdults)].map((_, adultIndex) => {
                  const saved = savedPax[adultIndex] ?? {};
                  let paxObj = {
                    firstName: saved.firstName || "",
                    lastName: saved.lastName || "",
                    title: saved.title || "Mr.",
                    guestType: "Adult",
                    guestInRoom: roomIndex + 1,
                  };
                  paxObj = HotelGuestReqFields(
                    paxObj,
                    guestRequiredFieldsForPax
                  );
                  if (saved.pan) paxObj.pan = saved.pan;
                  if (saved.passportNo) paxObj.passportNo = saved.passportNo;
                  paxListArr.push(paxObj);
                });
                [...Array(room.noOfChilds)].map((_, index) => {
                  const saved = savedPax[room.noOfAdults + index] ?? {};
                  let paxObj = {
                    firstName: saved.firstName || "",
                    lastName: saved.lastName || "",
                    title: saved.title || "Mstr",
                    guestType: "Child",
                    guestInRoom: roomIndex + 1,
                    age: parseInt(room.childAge[index]),
                  };
                  paxObj = HotelGuestReqFields(
                    paxObj,
                    guestRequiredFieldsForPax
                  );
                  if (saved.pan) paxObj.pan = saved.pan;
                  if (saved.passportNo) paxObj.passportNo = saved.passportNo;
                  paxListArr.push(paxObj);
                });

                roomInfoArr.push({ paxInfoList: paxListArr });
              });

              // OLD (removed): let { pan } = hotels.guestRequiredFields[0];
              applyPanRequiredForCheckout({
                guestRequiredFields: guestRequiredFieldsForPax,
                nationality: hotelParams?.nationality,
                setIsPanRequired,
                isMasterMember,
                isAgent: user?.Role?.RoleId === 5 || agent,
                // Guest (RoleType = 4 / without login): PAN mandatory for International only.
                isGuest: Number(user?.Role?.RoleId ?? (agent ? 5 : 4)) === 4,
                isInternationalHotel: isInternationalHotelCheckoutReview({
                  hotelPriceData: hotelsWithSelectionFlags || {},
                }),
                rooms:
                  hotelsWithSelectionFlags?.rooms ??
                  hotelsWithSelectionFlags?.Rooms ??
                  [],
              });
              setRoomGuestInfo(roomInfoArr);
              if (guestDraft?.specialRequest) {
                setSpecialRequest(String(guestDraft.specialRequest));
              }
              const restoredNames = {};
              roomInfoArr.forEach((room, roomIndex) => {
                (room.paxInfoList ?? []).forEach((pax, paxIndex) => {
                  restoredNames[`Title_${roomIndex}_${paxIndex}`] = pax.title;
                  restoredNames[`firstname_${roomIndex}_${paxIndex}`] =
                    pax.firstName;
                  restoredNames[`lastname_${roomIndex}_${paxIndex}`] =
                    pax.lastName;
                  if (pax.passportNo) {
                    restoredNames[`passportNo_${roomIndex}_${paxIndex}`] =
                      pax.passportNo;
                  }
                });
              });
              if (Object.keys(restoredNames).length > 0) {
                guestDetailsForm.setFieldsValue(restoredNames);
              }

              // OLD (removed):
              // No persisted checkout snapshot, so refresh after transient supplier error could show empty page.
              // NEW:
              // Cache last valid checkout payload for this trace/roomPlan and restore when refresh reprice fails.
              try {
                sessionStorage.setItem(
                  cacheKey,
                  JSON.stringify({
                    hotelPriceData: {
                      ...hotelsWithSelectionFlags,
                      purchaseType,
                      tripjackIsBA:
                        tripjackIsBA === true
                          ? true
                          : tripjackIsBA === false
                            ? false
                            : payload.tripjackIsBA ??
                              hotelsWithSelectionFlags?.tripjackIsBA,
                      traceId,
                      repriceId,
                      tripjackBookingId: payload.tripjackBookingId ?? null,
                      reviewReady: payload.reviewReady ?? false,
                    },
                    hotelSearchData: requestData,
                    roomGuestInfo: roomInfoArr,
                    selectedHotelName:
                      selectedHotelName ||
                      hotelsWithSelectionFlags?.hotelName ||
                      "",
                  })
                );
              } catch {
                // ignore cache write failures
              }
            } else if (payload.errors && payload.errors.length > 0) {
              payload.errors.forEach((err) => {
                if (err.errorCode === "SOLDOUT") {
                  message.error(err.errorDetail, 5);
                }
              });
            }
          }
        } else {
          // Handle non-200 status or error message
          if (res.message) {
            message.error(res.message, 5);
          } else if (payload?.errors && payload.errors.length > 0) {
            payload.errors.forEach((err) => {
              if (err.errorCode === "SOLDOUT") {
                message.error(err.errorDetail, 5);
              }
            });
          }
          if (hydrateFromCheckoutCache(cacheKey, hotelParams)) {
            return;
          }
          const errMsg =
            res?.message ||
            payload?.message ||
            "Live supplier price could not be loaded. The room may still be available — go back, wait for the price on the room page, then open checkout again.";
          setCheckoutError(errMsg);
          message.warning(errMsg, 6);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (hydrateFromCheckoutCache(cacheKey, hotelParams)) {
          return;
        }
        const errMsg =
          err?.message?.includes("abort") || /timeout/i.test(String(err?.message ?? ""))
            ? "Price request timed out. Supplier is slow — wait on the room page until price appears, then try checkout again."
            : err?.message ||
              "Unable to load checkout price. Go back to the room page and try again.";
        setCheckoutError(errMsg);
        message.warning(errMsg, 6);
        setLoading(false);
      });
  };

  const handleInsuranceChange = (val) => {
    if (val === 1) {
      setSelectedInsuranceData(insuranceData);
    } else {
      setSelectedInsuranceData({ amount: 0, insuranceCoverage: 0, status: 0 });
    }
    setInsuranceRequired(val);
  };

  const redirectToPreview = () => {
    contact_form
      .validateFields()
      .then((val) => {
        guestDetailsForm
          .validateFields()
          .then((passegersData) => {
            let countryName = "";
            if (val.countryCode) {
              countryName = CountryList.filter(
                (item) => item.code === val.countryCode
              );
              countryName = countryName[0].name;
            }

            const guestsForPreview = (roomGuestInfo ?? []).map((room, roomIndex) => ({
              ...room,
              paxInfoList: (room.paxInfoList ?? []).map((pax, paxIndex) => ({
                ...pax,
                title:
                  passegersData?.[`Title_${roomIndex}_${paxIndex}`] ??
                  pax.title,
                firstName: String(
                  passegersData?.[`firstname_${roomIndex}_${paxIndex}`] ??
                    pax.firstName ??
                    "",
                ).trim(),
                lastName: String(
                  passegersData?.[`lastname_${roomIndex}_${paxIndex}`] ??
                    pax.lastName ??
                    "",
                ).trim(),
                passportNo:
                  passegersData?.[`passportNo_${roomIndex}_${paxIndex}`] ??
                  pax.passportNo,
              })),
            }));
            if (SamePanNumber && panVerificationDetail?.[0]?.pan_number) {
              guestsForPreview.forEach((room) => {
                (room.paxInfoList ?? []).forEach((pax) => {
                  pax.pan = panVerificationDetail[0].pan_number;
                });
              });
            } else if (Array.isArray(panVerificationDetail)) {
              panVerificationDetail.forEach((item, i) => {
                const room = guestsForPreview[i];
                if (!room || !item?.pan_number) return;
                (room.paxInfoList ?? []).forEach((pax) => {
                  pax.pan = item.pan_number;
                });
              });
            }
            const hotelParams = queryString.parse(window.location.search);
            const addressInfo = {
              addressLine1: val.addressLine1,
              addressLine2: val.addressLine1,
              cellCountryCode: val.areaCode,
              countryCode: val.countryCode,
              areaCode: val.areaCode,
              phoneNo: val.phoneNo,
              email: val.email,
              city: val.city,
              state: val.state,
              country: countryName,
              zipCode: val.zipCode,
            };
            const data = {
              hotelPriceData: {...hotelPriceData, hotelName: selectedHotelName || hotelPriceData.hotelName},
              hotelSearchData,
              addressInfo,
              guests: guestsForPreview,
              insuranceRequired: passegersData?.insuranceRequired === 1 ? 1 : 0,
              insuranceData: selectedInsuranceData,
              samePan: SamePanNumber,
              specialRequest: String(specialRequest ?? "").trim(),
            };
            writeHotelCheckoutGuestDraft(hotelParams?.traceId, hotelParams?.hotelId, {
              guests: guestsForPreview,
              addressInfo,
              specialRequest: data.specialRequest,
            });
            setHotelCheckOutData(data);

            // Cache passport numbers in panvarify (like PAN) for fast re-fetch.
            collectPassportCachePayloads(roomGuestInfo).forEach((payload) => {
              ApiClient.post("hotels-v2/verificationPassport/", payload).catch(
                () => {},
              );
            });

            navigate("/hotels/hotel-review");
          })
          .catch((e) => {
            if (e?.errorFields && e?.errorFields.length > 0)
              guestDetailsForm.scrollToField(e.errorFields[0].name);
          });
      })
      .catch((e) => {
        if (!e.errorFields) {
          return;
        }
        contact_form.scrollToField(e.errorFields[0].name);
      });
  };

  // const getAdultChildCount = (paxInfo) => {
  //   if (paxInfo.length > 0) {
  //     let count = paxInfo.reduce((p, c) => {
  //       let type = c.guestType;
  //       if (!p.hasOwnProperty(type)) {
  //         p[type] = 0;
  //       }
  //       p[type]++;
  //       return p;
  //     }, {});

  //     return `Adult(s) ${count.Adult ? count.Adult : " "} ${
  //       count.Child ? ", Children " + count.Child : " "
  //     }`;
  //   }
  // };

  const gotoHotelDetail = (hotelCode) => {
    const hotelParams = queryString.parse(window.location.search);
    // OLD (removed): hotelId: hotelCode only — hotelCode from price payload may differ from search URL id.
    let queryObj = {
      hotelId: hotelParams?.hotelId || hotelCode,
      traceId: ids.traceId || hotelParams?.traceId,
      hotelName: selectedHotelName || hotelPriceData.hotelName,
    };
    const query = queryString.stringify(queryObj);
    navigate(`/hotels/hotel-details?${query}`);
  };

  const displayCurrentPan = (panRecord, roomIndex) => {
    let currentPan, allPanRecord;
    if(panRecord && panRecord.length > 0) {
      allPanRecord = panRecord?.filter(x=>x.roomIndex==roomIndex);
      return allPanRecord[allPanRecord.length - 1]?.pan_number;
    }else{
      return '';
    }
  }

  const handleBackBtn = () => {
    // OLD (removed): navigate(-1) could land on a stale history entry
    // with missing/incorrect query params, causing "Selected Rooms Not Found".
    // OLD (removed): hotelParams?.roomsId first — roomsId is HCI price-session id, not search hotelCode.
    // NEW: always return with search hotelId from checkout URL / loaded price data.
    const hotelParams = queryString.parse(window.location.search);
    const hotelId = hotelParams?.hotelId || hotelPriceData?.hotelCode;
    const traceId = hotelParams?.traceId || ids?.traceId;
    const queryObj = {
      hotelId,
      traceId,
      hotelName:
        hotelParams?.hotelName || selectedHotelName || hotelPriceData?.hotelName,
    };
    const query = queryString.stringify(queryObj);
    navigate(`/hotels/hotel-details?${query}`);
  }

  if (loading) {
    return (
      <div className="hotel-checkout-wrapper">
        <div className="form-header">
          <div className="form-header-container ">
            <div className="form-header-text">
              <h2>Fill out the form below and book your stay now!</h2>
            </div>
          </div>
        </div>
        <div className="form-body hotel-checkout-page-loading">
          <div className="form-body-container">
            <HotelSearchLoadingOverlay
              visible
              placement="inline"
              messages={HOTEL_CHECKOUT_LOADING_MESSAGES}
              subtitle="Checking availability, latest price & cancellation policy."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hotel-checkout-wrapper">
      <div className="form-header">
        <div className="form-header-container ">
          <div className="form-header-text">
            <h2>Fill out the form below and book your stay now!</h2>
          </div>
        </div>
      </div>
      {Object.keys(hotelPriceData).length > 0 ? (
      <div className="form-body">
        <div className="form-body-container">
          <Row gutter={[16, 16]}>
            <Col md={16} sm={24} xs={24}>
              <div className="hotel-detail-header-wrapper">
                <p className="booking-summary-div">Hotel Details</p>

                {hotelPriceData?.hotelCode && (
                  <p
                    className="pointer_cursor"
                    onClick={() => gotoHotelDetail(hotelPriceData.hotelCode)}
                  >
                    Change Room
                  </p>
                )}
              </div>

              {loading ? (
                <Card bordered={false} className="hotel-card-wrapper">
                  <Skeleton active />
                </Card>
              ) : Object.keys(hotelPriceData).length > 0 ? (
                <React.Fragment>
                <Card bordered={false} className="hotel-card-wrapper">
                  <HotelDetailsBox
                    Ids={ids}
                    hotelDetailsObj={{...hotelPriceData, hotelName: selectedHotelName || hotelPriceData.hotelName}}
                    hotelSearchData={hotelSearchData}
                  />
                </Card>
                
                {hotelPriceData?.rooms?.length > 0 && (
                  <React.Fragment>
                  <h5>Room Details</h5>
                  <RoomDetailsCard RoomDetails={hotelPriceData}/>
                </React.Fragment>
                )}

                <HotelSpecialRequest
                  value={specialRequest}
                  onChange={setSpecialRequest}
                />
                </React.Fragment>
              ) : null}

              <div className="contact-header">
                <div className="contact-title">
                  <p className="booking-summary-div">Guests Details</p>
                  { roomGuestInfo?.length > 0 && isPanRequired &&
                    <Col md={8} sm={12} >
                      <Checkbox onChange={(e) => setSamePanNumber(e.target.checked)}><strong >Same Pan Card For All Room</strong></Checkbox>
                    </Col>}
                </div>
              </div>

              {loading ? (
                <Card bordered={false} className="hotel-card-wrapper">
                  <Skeleton active />
                </Card>
              ) : roomGuestInfo?.length > 0 ? (
                <Form
                  form={guestDetailsForm}
                  scrollToFirstError={true}
                  layout="vertical"
                >
                  {roomGuestInfo.map((roomsObj, roomIndex) => (
                    <Card
                      bordered={false}
                      className="guest-details-form hotel-card-wrapper"
                      key={roomIndex}
                    >

                      <p className="room-title" style={{ display: "flex", justifyContent: "space-between" }}>Room {1 + roomIndex} 
                      <span style={{ color: "red"}}>{displayCurrentPan(panVerificationDetail, roomIndex)}</span>
                      </p>

                      {roomsObj.paxInfoList.map((pax, paxIndex) => (
                        <div
                          key={roomIndex + "detials" + paxIndex}
                          className="guest-input-wrapper"
                        >
                          <p className="guestsType">
                            {pax.guestType === "Adult" ? "Adult" : "Child"}{" "}

                          </p>

                          <Row gutter={[16, 16]}>
                            <Col md={3} sm={12} xs={24}>
                              {pax.guestType === "Adult" ? (
                                <Form.Item
                                  name={`Title_${roomIndex}_${paxIndex}`}
                                  label="Title"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Required",
                                    },
                                  ]}
                                >
                                  <Select
                                    onChange={(val) => {
                                      handlePaxField(
                                        val,
                                        roomIndex,
                                        paxIndex,
                                        "title"
                                      );
                                    }}
                                  >
                                    <Option value="Mr">Mr</Option>
                                    <Option value="Ms">Ms</Option>
                                    <Option value="Mrs">Mrs</Option>
                                  </Select>
                                </Form.Item>
                              ) : (
                                <Form.Item
                                  name={`Title_${roomIndex}_${paxIndex}`}
                                  label="Title"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Required",
                                    },
                                  ]}
                                >
                                  <Select
                                    onChange={(val) => {
                                      handlePaxField(
                                        val,
                                        roomIndex,
                                        paxIndex,
                                        "title"
                                      );
                                    }}
                                  >
                                     {/* <Option value="Mr">Mr</Option> */}
                                     <Option value="Miss">Miss</Option>
                                    <Option value="Mstr">Mstr</Option>
                                  </Select>
                                </Form.Item>
                              )}
                            </Col>

                            <Col md={6} sm={12} xs={24}>
                              <Form.Item
                                label="First Name"
                                name={`firstname_${roomIndex}_${paxIndex}`}
                                rules={hotelGuestNameRules}
                              >
                                <Input
                                  onChange={(e) => {
                                    handlePaxField(
                                      e.target.value,
                                      roomIndex,
                                      paxIndex,
                                      "firstName"
                                    );
                                  }}
                                  initialValue={panVerificationDetail && panVerificationDetail.length >= 1 ? panVerificationDetail?.filter(x => x.roomIndex == roomIndex && x.paxIndex == paxIndex)[0]?.pan_number : ''}
                                  disabled={(SamePanNumber == true && roomIndex == 0 && paxIndex == 0 && isPanRequired) || (SamePanNumber == false && paxIndex == 0) && isPanRequired ? true : false}
                                />
                              </Form.Item>
                            </Col>

                            <Col md={6} sm={12} xs={24}>
                              <Form.Item
                                label="Last Name"
                                name={`lastname_${roomIndex}_${paxIndex}`}
                                rules={hotelGuestNameRules}
                              >
                                <Input
                                  onChange={(e) => {
                                    handlePaxField(
                                      e.target.value,
                                      roomIndex,
                                      paxIndex,
                                      "lastName"
                                    );
                                  }}
                                  disabled={(SamePanNumber == true && roomIndex == 0 && paxIndex == 0  && isPanRequired) || (SamePanNumber == false && paxIndex == 0) && isPanRequired ? true : false}
                                />
                              </Form.Item>
                            </Col>

                            {/* {pax.hasOwnProperty("pan") && SamePanNumber === false && ( */}
                            {/* {paxIndex==0 &&(
                              <Col md={6} sm={12} xs={24}>
                       {roomIndex >=1 &&  SamePanNumber === true && (
                                <PanVerification /> 
                                )}
                                
                              </Col>)} */}
                            <Col md={7} sm={12} xs={24}>
                            {paxIndex ==0 && isPanRequired && (

                                SamePanNumber ==false || (SamePanNumber ==true && roomIndex==0) ?(
                                  <PanVerification serviceType={2} paxIndex={paxIndex} roomIndex={roomIndex} />

                                  ):(" ")
                              )}
                            {/* Same row as Title / First / Last (PAN slot) — passport for non-IN guests. */}
                            {pax.hasOwnProperty("passportNo") &&
                              !(user?.Role?.RoleId === 5 || agent) && (
                                <Form.Item
                                  label="Passport Number"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Required",
                                    },
                                  ]}
                                  name={`passportNo_${roomIndex}_${paxIndex}`}
                                >
                                  <Input
                                    className="inputbg"
                                    placeholder="Passport Number"
                                    onChange={(e) =>
                                      handlePaxField(
                                        e.target.value,
                                        roomIndex,
                                        paxIndex,
                                        "passportNo"
                                      )
                                    }
                                  />
                                </Form.Item>
                              )}
                            </Col>
                            {/* )} */}

                            {/* {pax.guestType === "Child" ? (
                              <Col md={5} sm={12} xs={24}>
                                <Form.Item
                                  name={`childAge_${roomIndex}_${paxIndex}`}
                                  label="Child Age"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Required",
                                    },
                                  ]}
                                >
                                  <Select
                                    onChange={(val) => {
                                      handlePaxField(
                                        val,
                                        roomIndex,
                                        paxIndex,
                                        "age"
                                      );
                                    }}
                                  >
                                    {[...Array(12)].map((_, i) => (
                                      <Option key={i + "age"} value={i}>
                                        {i} Years
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                            ) : (
                              <Col md={3} sm={12} xs={24}>
                                <Form.Item
                                  name={`adultage_${roomIndex}_${paxIndex}`}
                                  label="Age"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Required",
                                    },
                                  ]}
                                >
                                  <Select
                                    onChange={(val) => {
                                      handlePaxField(
                                        val,
                                        roomIndex,
                                        paxIndex,
                                        "age"
                                      );
                                    }}
                                  >
                                    {[...Array(83)].map((_, i) => (
                                      <Option
                                        key={i + 12}
                                        value={i + 12}
                                      >
                                        {i + 12}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                            )} */}

                            {pax.hasOwnProperty("passportDOI") && (
                              <Col md={8} sm={12} xs={24}>
                                <Form.Item
                                  label="Passport DOI"
                                  className="passport-dates"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Required",
                                    },
                                  ]}
                                  name={`passportDOI_${roomIndex}_${paxIndex}`}
                                >
                                  <DatePicker
                                    onChange={(date, dateString) =>
                                      handlePaxField(
                                        dateString,
                                        paxIndex,
                                        "passportDOI"
                                      )
                                    }
                                    format={"YYYY-MM-DD"}
                                    style={{ width: "100%" }}
                                  />
                                </Form.Item>
                              </Col>
                            )}

                            {pax.hasOwnProperty("passportDOE") && (
                              <Col md={8} sm={12} xs={24}>
                                <Form.Item
                                  label="Passport DOE"
                                  className="passport-dates"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Required",
                                    },
                                  ]}
                                  name={`passportDOE_${roomIndex}_${paxIndex}`}
                                >
                                  <DatePicker
                                    onChange={(_, dateString) =>
                                      handlePaxField(
                                        dateString,
                                        paxIndex,
                                        "passportDOE"
                                      )
                                    }
                                    format={"YYYY-MM-DD"}
                                    style={{ width: "100%" }}
                                  />
                                </Form.Item>
                              </Col>
                            )}
                          </Row>
                        </div>
                      ))}

                    </Card>
                  ))}{" "}
                  {insuranceData?.status === 1 &&
                    insuranceData?.serviceType === 2 ? (
                    <Card
                      bordered={false}
                      className="insurance-section hotel-card-wrapper"
                    >
                      <h5>THE SMART INSURANCE COVER</h5>
                      <p>EASY CLAIM PROCESS | NO QUESTIONS ASKED</p>

                      <Row gutter={[16, 16]} align="stretch" className="mt-4">
                        <Col md={8} sm={24} xs={24}>
                          <div className="insurance-box">
                            {insuranceData.description_box_1}
                          </div>
                        </Col>
                        <Col md={8} sm={24} xs={24}>
                          <div className="insurance-box">
                            {insuranceData.description_box_2}
                          </div>
                        </Col>
                        <Col md={8} sm={24} xs={24}>
                          <div className="insurance-box">
                            {insuranceData.description_box_3}
                          </div>
                        </Col>
                      </Row>

                      <div className="insurance-coverage">
                        <SafetyCertificateOutlined />
                        <span>
                          Insurance Coverage Amount :{" "}
                          {insuranceData.insuranceCoverage}
                        </span>
                      </div>

                      <Form.Item
                        name="insuranceRequired"
                        rules={[
                          {
                            required: true,
                            message: "Please Select Insurance",
                          },
                        ]}
                      >
                        <Radio.Group
                          buttonStyle="solid"
                          onChange={(e) =>
                            handleInsuranceChange(e.target.value)
                          }
                          className="insurance-radio-wrapper"
                        >
                          <Row gutter={[16, 16]}>
                            <Col md={10} sm={24} xs={24}>
                              <Radio
                                className={`radio-btn btn-insure  ${
                                  insuranceRequired === 1
                                    ? "btn-insure-active"
                                    : ""
                                  }`}
                                value={1}
                              >
                                Insure For ₹{insuranceData.amount}/pax
                              </Radio>
                            </Col>

                            <Col md={10} sm={24} xs={24}>
                              <Radio
                                value={0}
                                className={`radio-btn btn-risk ${
                                  insuranceRequired === 0
                                    ? "btn-risk-active"
                                    : ""
                                  }`}
                              >
                                I'll Risk it
                              </Radio>
                            </Col>
                          </Row>
                        </Radio.Group>
                      </Form.Item>
                    </Card>
                  ) : null}
                </Form>
              ) : null}

             <div className="contact-header"> 
                <div className="contact-title">
                  <p className="booking-summary-div">Contact Details</p>
                </div>
              </div>
              <Card
                bordered={false}
                className="guest-details-form hotel-card-wrapper"
              >
                <Form
                  layout="vertical"
                  name="contactForm"
                  form={contact_form}
                  scrollToFirstError={true}
                  initialValues={{ areaCode: agent ? "+91" : "" }}
                >
                  <div className="guest-input-wrapper">
                    <Row gutter={16}>
                      <Col md={8} sm={12} xs={24}>
                        <Form.Item
                          label="Email"
                          name="email"
                          rules={[
                            { required: true, message: "Required" },
                            { type: "email", message: "Invalid Email" },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col md={8} sm={12} xs={24}>
                        <Form.Item
                          label="Phone number"
                          className="phno phone-number-row"
                          required
                        >
                          <Space.Compact block className="phone-number-compact">
                            <Form.Item
                              name="areaCode"
                              className="phone-country-item"
                              rules={[
                                {
                                  required: true,
                                  message: "Phone Number Code Required",
                                },
                              ]}
                              noStyle
                            >
                              <Select
                                showSearch
                                optionLabelProp="label"
                                placeholder="Code"
                                className="phone-country-select"
                                popupMatchSelectWidth={300}
                                filterOption={(input, option) =>
                                  String(option?.label ?? option?.children ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              >
                                {CountryList.map((item) => (
                                  <Option
                                    key={`${item.code}-${item.dial_code}`}
                                    value={item.dial_code}
                                    label={formatCountryDialLabel(item)}
                                  >
                                    {formatCountryDialLabel(item)}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              name="phoneNo"
                              className="phone-number-item"
                              rules={[
                                {
                                  required: true,
                                  message: "Required",
                                },
                                {
                                  minLength: 10,
                                  maxLength: 10,
                                  pattern: "^[0-9]{10}$",
                                  message: "Must be 10 digits",
                                },
                              ]}
                              noStyle
                            >
                              <Input
                                className="phone-number-input"
                                inputMode="numeric"
                                maxLength={10}
                                placeholder="10-digit mobile"
                              />
                            </Form.Item>
                          </Space.Compact>
                        </Form.Item>
                      </Col>
  {/*
                      <Col md={8} sm={12} xs={24}>
                        <Form.Item
                          name="addressLine1"
                          label="Address"
                          rules={[
                            {
                              required: true,
                              message: "Required",
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      <Col md={8} sm={12} xs={24}>
                        <Form.Item
                          name="city"
                          label="City"
                          rules={[
                            {
                              required: true,
                              message: "Required",
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      <Col md={8} sm={12} xs={24}>
                        <Form.Item
                          name="state"
                          label="State"
                          rules={[
                            {
                              required: true,
                              message: "Required",
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      <Col md={8} sm={12} xs={24}>
                        <Form.Item
                          name="zipCode"
                          label="ZIP/Postal code"
                          rules={[
                            {
                              required: true,
                              message: "Required",
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      <Col md={8} sm={12} xs={24}>
                        <Form.Item
                          name="countryCode"
                          label="Country"
                          rules={[
                            {
                              required: true,
                              message: "Required",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Select Country"
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {CountryList.map((item) => (
                              <Option key={item.code} value={item.code}>
                                {item.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>  */}
                    </Row>
                  </div>
                </Form>
              </Card>
          
            </Col>

            <Col md={8} sm={24} xs={24}>
              <div className="hotel-price-wrapper">
                {loading ? (
                  <Card bordered={false} className="hotel-card-wrapper">
                    <Skeleton active />
                  </Card>
                ) : Object.keys(hotelPriceData).length > 0 ? (
                  <HotelFairBox
                    hotelDetailsObj={hotelPriceData}
                    hotelSearchData={hotelSearchData}
                    isPromoVisible={true}
                  />
                ) : null}
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col sm={24} md={16}>
              <HotelBookPayCard
                isLoading={loading}
                pgIsLoading={false}
                holdLoading={false}
                purchaseType={false}
                bookpaycardinfo={"hotel-checkout"}
                redirectToPreview={redirectToPreview}
                agent={false}
              />
            </Col>
          </Row>
        </div>
      </div>
      ) : (
      <div className="container">
        <Row gutter={[16, 16]}>
          <Col md={24} sm={24} xs={24} className="border my-2">
            <Result
              status="warning"
              title={
                checkoutError ||
                "Room Not Available Please Try Again."
              }
              subTitle={
                checkoutError
                  ? "Free cancellation on the room page does not guarantee checkout if the supplier price session is slow or expired. Reselect the room and wait for the price to finish loading."
                  : undefined
              }
              extra={
                <Button type="primary" key="console" onClick={handleBackBtn}>
                  Back To Room Page
                </Button>
              }
            />
          </Col>
        </Row>
      </div>
      )}
    </div>
  );
};

export default HotelCheckout;
